import { useDispatch, useSelector } from "react-redux";
import Footer from "../../SharedModule/components/Footer";
import { verifyModuleAccess } from "../../SharedModule/utils/accessUtils";
import { Spinner } from "../../SharedModule/components/Spinner";
import {
  CLIENTS_ALL_ROLES,
  RECURRING_BILLING_GROUP_ID,
  ROUTE_CLIENTS,
  ROUTE_CLIENTS_INVOICES,
  ROUTE_CLIENTS_RELATIONSHIP_REVIEW,
  ROUTE_INVOICES_DETAIL,
} from "../../SharedModule/utils/constants";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";
import { useLocation, useNavigate, useParams } from "react-router";
import { setActiveTab } from "../../SharedModule/redux/reducers/uiShared.reducer";
import { useEffect, useState } from "react";
import HeaderClientsModule from "../components/HeaderClientsModule";
import { Calendar } from "../../SharedModule/components/Calendar";
import { Invoice } from "../../SharedModule/types/shared-module-types";
import {
  currencyFormat,
  dateFormat,
} from "../../SharedModule/utils/formatters";
import { useClientInvoices } from "../hooks/useClientInvoices";
import { getBillingGroupUrl } from "../../BillingModule/utils/billingUtils";
import {
  changeOrder,
  initialState,
  SelectOrderCriteria,
  setClientInvoicesFilters,
} from "../redux/reducers/clientInvoices.reducer";
import { NoContent } from "../../SharedModule/components/NoContent";
import image from "./../../styles/legacy/404.png";
import { ExternalLink } from "../../SharedModule/components/ExternalLink";
import { resetScrollY } from "../../SharedModule/utils/pagesUtils";

export const ClientInvoicesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const sortBy = useSelector(SelectOrderCriteria);

  const { id } = useParams();

  // If there is no id, go to clients list
  if (!id) {
    navigate(ROUTE_CLIENTS);
  }

  const {
    isLoadingClientInvoices,
    invoicesInfo,
    clientInvoicesFilters,
    userRolesList,
    rolesLoaded,
  } = useClientInvoices(Number(id));

  useEffect(() => {
    document.title = "The Hub - Client Invoices";

    dispatch(
      setClientInvoicesFilters({
        dateFrom: startDate,
        dateTo: endDate,
        accountIds: [Number(id)],
        statusIds: [3],
      })
    );

    dispatch(setActiveTab(ROUTE_CLIENTS_INVOICES));

    return () => {
      resetScrollY(globalThis.location.pathname);
      // reset filters if user got to another page
      if (
        id &&
        globalThis.location.pathname !==
          ROUTE_CLIENTS_INVOICES.replace(":id", id)
      ) {
        dispatch(setClientInvoicesFilters(initialState.clientInvoicesfilters));
      }
    };
  }, []);

  const [startDate, setStartDate] = useState(clientInvoicesFilters.dateFrom);
  const [endDate, setEndDate] = useState(clientInvoicesFilters.dateTo);
  const [formTouched, setFormTouched] = useState(false);

  const searchInvoices = () => {
    dispatch(
      setClientInvoicesFilters({
        dateFrom: startDate,
        dateTo: endDate,
        accountIds: [Number(id)],
        statusIds: [3],
      })
    );
  };

  return (
    <div className="content">
      <HeaderClientsModule
        isLoadingPage={isLoadingClientInvoices || !rolesLoaded}
        backFromRelationshipPage={
          location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
        }
      />

      {(isLoadingClientInvoices || !rolesLoaded) && (
        <Spinner
          style={{
            marginLeft: "50%",
            marginTop: "5%",
          }}
        />
      )}

      {!isLoadingClientInvoices &&
        rolesLoaded &&
        !verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
          <ForbiddenPage />
        )}

      {!isLoadingClientInvoices &&
        rolesLoaded &&
        verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
          <div style={{ margin: "1% 8%" }}>
            <div className="d-flex justify-content-between">
              <h3 style={{ marginTop: "0.5rem" }}>Invoices</h3>
              <div className="d-flex">
                <label
                  htmlFor="client-invoices-from"
                  className="form-label"
                  style={{ marginTop: "15px" }}
                >
                  From
                </label>
                <Calendar
                  id="client-invoices-from"
                  className="ms-3"
                  date={startDate}
                  maxDate={new Date(endDate)}
                  onChange={(start) => {
                    setStartDate(start);
                    setFormTouched(true);
                  }}
                />
                <label
                  htmlFor="client-invoices-to"
                  className="form-label ms-5"
                  style={{ marginTop: "15px" }}
                >
                  To
                </label>
                <Calendar
                  id="client-invoices-to"
                  className="ms-3"
                  date={endDate}
                  minDate={new Date(startDate)}
                  onChange={(end) => {
                    setEndDate(end);
                    setFormTouched(true);
                  }}
                />
                <button
                  className="btn btn-primary ms-5"
                  onClick={searchInvoices}
                  disabled={startDate > endDate || !formTouched}
                >
                  Filter Invoices
                </button>
              </div>
            </div>

            {isLoadingClientInvoices && (
              <Spinner
                style={{
                  marginLeft: "50%",
                  marginTop: "5%",
                }}
              />
            )}

            {!isLoadingClientInvoices && invoicesInfo?.length === 0 && (
              <div className="py-5">
                <NoContent
                  title="No invoices"
                  text="Edit your filters to find the invoices you are looking for."
                  image={image}
                  style={{ margin: "0 auto" }}
                />
              </div>
            )}

            {!isLoadingClientInvoices && invoicesInfo?.length > 0 && (
              <table className="table grey-table-header-background mt-3">
                <thead className="align-middle">
                  <tr className="sticky sticky-header">
                    <th
                      className={`border-dark border-top fw-normal py-0 ps-3 pointer column-15 ${
                        sortBy.orderBy === "id"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      onClick={() => dispatch(changeOrder({ orderBy: "id" }))}
                    >
                      Invoice ID
                    </th>
                    <th
                      className={`border-dark border-top fw-normal py-0 ps-3 pointer column-15 ${
                        sortBy.orderBy === "date"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      onClick={() =>
                        dispatch(
                          changeOrder({ orderBy: "date", dataType: "date" })
                        )
                      }
                    >
                      Invoice Date
                    </th>
                    <th
                      className={`border-dark border-top fw-normal py-0 ps-3 pointer column-55 ${
                        sortBy.orderBy === "billingGroup.name"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      onClick={() =>
                        dispatch(
                          changeOrder({
                            orderBy: "billingGroup.name",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Client & Project/WO
                    </th>
                    <th
                      className={`border-dark border-top fw-normal py-0 pe-4 pointer text-end column-15 ${
                        sortBy.orderBy === "amount"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      onClick={() =>
                        dispatch(changeOrder({ orderBy: "amount" }))
                      }
                    >
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoicesInfo?.map((invoice: Invoice) => {
                    return (
                      <tr key={invoice.id}>
                        <td className="ps-3">
                          <ExternalLink
                            className="link-text fw-500"
                            to={ROUTE_INVOICES_DETAIL.replace(
                              ":id",
                              invoice.id.toString()
                            )}
                          >
                            {invoice.id}
                          </ExternalLink>
                          <div>
                            {invoice.quickBooksNumber && (
                              <div>({invoice.quickBooksNumber})</div>
                            )}
                          </div>
                        </td>
                        <td className="ps-3">{dateFormat(invoice.date)}</td>
                        <td className="ps-3">
                          <div className="fw-bold">{invoice.account.name}</div>
                          {invoice.billingGroup.type.id ===
                          RECURRING_BILLING_GROUP_ID ? (
                            <span>{`${invoice.billingGroup.type.name} #${invoice.billingGroup.externalId} - ${invoice.billingGroup.name}`}</span>
                          ) : (
                            <ExternalLink
                              to={getBillingGroupUrl(invoice)}
                              className="mt-1 link-text fw-500"
                            >
                              {`${invoice.billingGroup.type.name} #${invoice.billingGroup.externalId} - ${invoice.billingGroup.name}`}
                            </ExternalLink>
                          )}
                        </td>
                        <td className="fw-bold ps-3 text-end">
                          {currencyFormat(invoice.amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      <Footer />
    </div>
  );
};
